'use client';

import { DisplayVariationAttributeType } from '@/react/view-models/ProductVM';
import { observer } from 'mobx-react-lite';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import { classes } from '@/next-utils/css-utils/scss-utils';
import type { BadgePosition } from '@/services/models/Product';
import { Link } from '../../core-ui/Link';
import { CommonErrorBoundary } from '../../errors/boundaries/CommonErrorBoundary';
import { Price } from '../Price';
import ProductDetailsVariant from '../ProductDetailsVariant';
import VariationSelector from '../VariationSelector';
import { WishlistHeart } from '../WishlistHeart';
import IVariationPanelProps from './IVariationPanelProps';
import InternalRatingStars from './InternalRatingStars';

import { useVariationSettings } from './useVariationSettings';
import { SuperGroupContainer } from '../SuperGroupContainer';
import { ProductBadge } from '../ProductBadge';

import S from './styles.base.module.scss';

const badgePositionStyleMap: Record<BadgePosition, string> = {
  'top-left': S.badgeTopLeft,
  'top-right': S.badgeTopRight
};

/** Variation panel to contain size/color selector, add to cart. */
export const DefaultVariationPanel = observer(function DefaultVariationPanel({
  variant = ProductDetailsVariant.Default,
  product,
  children,
  showSizeError,
  onClearSizeValidationError
}: IVariationPanelProps) {
  const {
    isWishlistEnabled,
    isShowStarRatings,
    name,
    sku,
    ratings,
    priceModel,
    groupName
  } = useVariationSettings(variant, product);

  // TODO: consider showing a spinner if a product update is loading
  // if (!product) return <Spinner size={30} color="#43A047" />;

  const bottomVariationTypes = [
    DisplayVariationAttributeType.Color,
    DisplayVariationAttributeType.Size
  ];
  const topVariationTypes = product.allVariationTypes.filter(
    (type) => !bottomVariationTypes.includes(type)
  );

  const isPDP = variant === ProductDetailsVariant.Default;
  const isQuickviewVariant =
    variant === ProductDetailsVariant.Quickview ||
    variant === ProductDetailsVariant.Update ||
    variant === ProductDetailsVariant.ExchangeQuickview;

  // Get strikethrough position for PDP from product config
  const productConfig = ConfigurationService.getConfig('product');
  // Group name only shows on PDP
  const shouldShowGroupName =
    productConfig.getSetting('group.displayGroupName.pdp').value &&
    !isNullish(groupName) &&
    isPDP;
  const groupNamePosition = productConfig.getSetting(
    'group.groupNamePosition.pdp'
  ).value;
  const strikethroughPosition = productConfig.getSetting(
    'pricing.strikethroughPosition.pdp'
  ).value;

  return (
    <div className={S.variationPanel}>
      {shouldShowGroupName && groupNamePosition === 'above' && (
        <div className={S.topSection}>
          <div className={S.group}>{groupName}</div>
        </div>
      )}

      <div className={S.nameSection}>
        <div className={S.name}>
          {shouldShowGroupName && groupNamePosition === 'inline' && (
            <>{groupName} </>
          )}
          {name}
        </div>
        {isWishlistEnabled && <WishlistHeart slug={sku} />}
      </div>

      <div className={S.reviewSection}>
        {isShowStarRatings ? (
          isPDP ? (
            <Link
              as="a"
              href="#reviews"
              className={S.link}
              onClick={() => {
                // TODO: refactor this to use a forward ref
                const el = document.querySelector(
                  '#reviews button'
                ) as HTMLElement;
                if (el) el.click();
              }}
            >
              <InternalRatingStars ratings={ratings} />
            </Link>
          ) : (
            <div>
              <InternalRatingStars ratings={ratings} />
            </div>
          )
        ) : undefined}
        <Price
          className={S.price}
          price={priceModel}
          strikethroughPosition={strikethroughPosition}
        />
      </div>

      {!isQuickviewVariant && !isNullOrEmpty(product.badgeText) && (
        <div
          className={classes(
            S.badgeContainer,
            product.badgePosition
              ? badgePositionStyleMap[product.badgePosition]
              : S.badgeTopLeft
          )}
        >
          <ProductBadge badgeText={product.badgeText} />
        </div>
      )}

      {!isQuickviewVariant && !isNullish(product.superGroup) && (
        <div className={S.superGroupSection}>
          <SuperGroupContainer
            key={product.superGroup.key}
            superGroup={product.superGroup}
            variant={variant}
          />
        </div>
      )}

      <div className={S.topVariations}>
        <CommonErrorBoundary>
          {topVariationTypes.map((type) => (
            <VariationSelector key={type} type={type} product={product} />
          ))}
        </CommonErrorBoundary>
      </div>

      {/*
        If child content is provided, it should be placed here. This
        would typically be the product carousel, passed in for
        mobile/tablet display.
        
        NOTE: On desktop, the carousel is displayed separately to the left
        of the VariationPanel and is NOT affected by component ordering.
        On mobile, the carousel is passed as {children} and WILL be affected
        by the .carouselSection order value.
      */}
      <div className={S.carouselSection}>{children}</div>

      <div className={S.bottomVariations}>
        <CommonErrorBoundary>
          {bottomVariationTypes.map((type) => (
            <VariationSelector
              key={type}
              type={type}
              product={product}
              showSizeError={showSizeError}
              onClearSizeValidationError={onClearSizeValidationError}
            />
          ))}
        </CommonErrorBoundary>
      </div>
    </div>
  );
});
